import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

/**
 * Document types that can be navigated to from reports
 */
export type DocumentType =
  // Vendor documents
  | 'vendor'
  | 'purchase_order'
  | 'vendor_invoice'
  | 'purchase_invoice'
  | 'vendor_payment'
  | 'debit_note'
  | 'grn'
  | 'item_receipt'
  | 'vendor_return'
  // Client documents
  | 'client'
  | 'sales_order'
  | 'client_invoice'
  | 'sales_invoice'
  | 'client_payment'
  | 'client_refund'
  | 'credit_note'
  | 'client_return'
  // Finance documents
  | 'journal_entry'
  | 'journal_voucher'
  // Inventory
  | 'inventory_adjustment'
  | 'inventory_ledger'
  // Ledgers
  | 'vendor_ledger'
  | 'client_ledger'
  | 'general_ledger'
  // Items
  | 'item'
  // BMW documents
  | 'bmw_collection_request'
  | 'bmw_disposal';

/**
 * Route configuration for each document type
 */
interface RouteConfig {
  basePath: string;
  /** The query parameter name for the document ID (e.g., 'invoiceId', 'billPaymentId') */
  idParamName: string;
  /** If true, uses path parameter instead of query params */
  usePathParam?: boolean;
  /** Custom path structure for path-based routes */
  pathStructure?: 'id-suffix' | 'id-view' | 'id-ledger';
}

/**
 * Mapping of document types to their route configurations
 */
const DOCUMENT_ROUTE_MAP: Record<DocumentType, RouteConfig> = {
  // Vendor documents
  vendor: { basePath: '/vendors/onboarding', idParamName: 'vendorId' },
  purchase_order: { basePath: '/vendors/purchase-order', idParamName: 'poId' },
  vendor_invoice: { basePath: '/vendors/purchase-invoice', idParamName: 'invoiceId' },
  purchase_invoice: { basePath: '/vendors/purchase-invoice', idParamName: 'invoiceId' },
  vendor_payment: { basePath: '/vendors/vendor-payment', idParamName: 'billPaymentId' },
  debit_note: { basePath: '/vendors/debit-note', idParamName: 'id' },
  grn: { basePath: '/vendors/grn/create', idParamName: 'grnId' },
  item_receipt: { basePath: '/vendors/grn/create', idParamName: 'grnId' },
  vendor_return: { basePath: '/vendors/return', idParamName: 'return_id' },

  // Client documents
  client: { basePath: '/clients/onboarding', idParamName: 'clientId' },
  sales_order: { basePath: '/clients/sales-order', idParamName: 'soId' },
  client_invoice: { basePath: '/clients/client-invoice', idParamName: 'invoiceId' },
  sales_invoice: { basePath: '/clients/client-invoice', idParamName: 'invoiceId' },
  client_payment: { basePath: '/clients/client-payment', idParamName: 'billPaymentId' },
  client_refund: { basePath: '/clients/refund-payment', idParamName: 'clientRefundId' },
  credit_note: { basePath: '/clients/credit-note', idParamName: 'id' },
  client_return: { basePath: '/clients/return', idParamName: 'id' },

  // Finance documents - uses path-based routing: /finance/journal-vouchers/{id}/view
  journal_entry: { basePath: '/finance/journal-vouchers', idParamName: 'id', usePathParam: true, pathStructure: 'id-view' },
  journal_voucher: { basePath: '/finance/journal-vouchers', idParamName: 'id', usePathParam: true, pathStructure: 'id-view' },

  // Inventory
  inventory_adjustment: { basePath: '/inventory-adjustment', idParamName: 'id' },
  inventory_ledger: { basePath: '/inventory', idParamName: 'id', usePathParam: true, pathStructure: 'id-ledger' },

  // Ledgers - these use path params
  vendor_ledger: { basePath: '/vendor-ledger', idParamName: 'id', usePathParam: true, pathStructure: 'id-suffix' },
  client_ledger: { basePath: '/client-ledger', idParamName: 'id', usePathParam: true, pathStructure: 'id-suffix' },
  general_ledger: { basePath: '/finance/general-ledger', idParamName: 'account_id' },

  // Items - uses item_id query param
  item: { basePath: '/items', idParamName: 'item_id' },

  // BMW documents
  bmw_collection_request: { basePath: '/collection-requests', idParamName: 'id', usePathParam: true, pathStructure: 'id-suffix' },
  bmw_disposal: { basePath: '/disposal', idParamName: 'id', usePathParam: true, pathStructure: 'id-suffix' },
};

/**
 * Navigation mode for the document
 */
export type NavigationMode = 'view' | 'edit';

/**
 * Options for document navigation
 */
export interface NavigationOptions {
  /** Navigation mode - defaults to 'view' */
  mode?: NavigationMode;
  /** Open in new tab */
  newTab?: boolean;
  /** Additional query parameters to pass */
  queryParams?: Record<string, string | number>;
}

/**
 * Service to navigate to documents from reports or other components.
 * Centralizes document navigation logic for consistent routing across the application.
 *
 * @example
 * // Navigate to a vendor invoice in view mode
 * this.documentNavigation.navigateTo('vendor_invoice', '123456789');
 *
 * @example
 * // Navigate to a client invoice in edit mode
 * this.documentNavigation.navigateTo('client_invoice', '123456789', { mode: 'edit' });
 *
 * @example
 * // Open vendor ledger in new tab
 * this.documentNavigation.navigateTo('vendor_ledger', '123456789', { newTab: true });
 */
@Injectable({
  providedIn: 'root',
})
export class DocumentNavigationService {
  constructor(private router: Router) {}

  /**
   * Navigate to a specific document in view or edit mode
   * @param documentType The type of document to navigate to
   * @param documentId The ID of the document
   * @param options Navigation options (mode, newTab, etc.)
   */
  navigateTo(
    documentType: DocumentType,
    documentId: string | number,
    options: NavigationOptions = {}
  ): void {
    const { mode = 'view', newTab = false, queryParams = {} } = options;

    const routeConfig = DOCUMENT_ROUTE_MAP[documentType];

    if (!routeConfig) {
      console.warn(`Unknown document type: ${documentType}`);
      return;
    }

    if (newTab) {
      // Open in new tab
      const url = this.getDocumentUrl(documentType, documentId, mode);
      const baseUrl = window.location.origin;
      window.open(`${baseUrl}${url}`, '_blank');
    } else {
      // Navigate within the app
      if (routeConfig.usePathParam) {
        this.navigateWithPathParam(routeConfig, documentId, mode);
      } else {
        this.navigateWithQueryParam(routeConfig, documentId, mode, queryParams);
      }
    }
  }

  /**
   * Navigate using path parameters (e.g., /finance/journal-vouchers/123/view)
   */
  private navigateWithPathParam(
    routeConfig: RouteConfig,
    documentId: string | number,
    mode: NavigationMode
  ): void {
    switch (routeConfig.pathStructure) {
      case 'id-view':
        // e.g., /finance/journal-vouchers/123/view
        this.router.navigate([routeConfig.basePath, String(documentId), mode]);
        break;
      case 'id-ledger':
        // e.g., /inventory/123/ledger
        this.router.navigate([routeConfig.basePath, String(documentId), 'ledger']);
        break;
      case 'id-suffix':
      default:
        // e.g., /vendor-ledger/123
        this.router.navigate([routeConfig.basePath, String(documentId)]);
        break;
    }
  }

  /**
   * Navigate using query parameters (e.g., /vendors/purchase-invoice?invoiceId=123&mode=view)
   */
  private navigateWithQueryParam(
    routeConfig: RouteConfig,
    documentId: string | number,
    mode: NavigationMode,
    additionalParams: Record<string, string | number>
  ): void {
    const queryParams: Record<string, string> = {
      [routeConfig.idParamName]: String(documentId),
      mode: mode,
      ...Object.fromEntries(
        Object.entries(additionalParams).map(([k, v]) => [k, String(v)])
      ),
    };

    this.router.navigate([routeConfig.basePath], { queryParams });
  }

  /**
   * Get the URL for a document without navigating
   * Useful for creating links or for programmatic URL generation
   * @param documentType The type of document
   * @param documentId The ID of the document
   * @param mode Navigation mode ('view' or 'edit')
   * @returns The URL string for the document
   */
  getDocumentUrl(
    documentType: DocumentType,
    documentId: string | number,
    mode: NavigationMode = 'view'
  ): string {
    const routeConfig = DOCUMENT_ROUTE_MAP[documentType];

    if (!routeConfig) {
      console.warn(`Unknown document type: ${documentType}`);
      return '';
    }

    if (routeConfig.usePathParam) {
      switch (routeConfig.pathStructure) {
        case 'id-view':
          return `${routeConfig.basePath}/${documentId}/${mode}`;
        case 'id-ledger':
          return `${routeConfig.basePath}/${documentId}/ledger`;
        case 'id-suffix':
        default:
          return `${routeConfig.basePath}/${documentId}`;
      }
    }

    const params = new URLSearchParams({
      [routeConfig.idParamName]: String(documentId),
      mode: mode,
    });
    return `${routeConfig.basePath}?${params.toString()}`;
  }

  /**
   * Check if a document type is supported for navigation
   * @param documentType The document type to check
   * @returns true if the document type is supported
   */
  isSupported(documentType: string): documentType is DocumentType {
    return documentType in DOCUMENT_ROUTE_MAP;
  }

  /**
   * Get all supported document types
   * @returns Array of supported document types
   */
  getSupportedDocumentTypes(): DocumentType[] {
    return Object.keys(DOCUMENT_ROUTE_MAP) as DocumentType[];
  }

  /**
   * Map backend search result entity types to frontend DocumentType.
   * This allows the global search to use the DocumentNavigationService for routing.
   * @param entityType The entity type from the backend search result
   * @returns The corresponding DocumentType, or null if not mappable
   */
  mapEntityTypeToDocumentType(entityType: string): DocumentType | null {
    const entityTypeMap: Record<string, DocumentType> = {
      // Vendor entities
      vendor: 'vendor',
      vendor_invoice: 'vendor_invoice',
      vendor_po: 'purchase_order',
      vendor_grn: 'grn',
      vendor_return: 'vendor_return',
      vendor_debit_note: 'debit_note',
      vendor_payment: 'vendor_payment',
      // Client entities
      client: 'client',
      client_invoice: 'client_invoice',
      client_order: 'sales_order',
      client_return: 'client_return',
      client_credit_note: 'credit_note',
      client_payment: 'client_payment',
      client_refund: 'client_refund',
      // Item
      item: 'item',
      // Finance
      journal_voucher: 'journal_voucher',
      // Inventory
      inventory_adjustment: 'inventory_adjustment',
      // BMW entities
      bmw_collection_request: 'bmw_collection_request',
      bmw_disposal: 'bmw_disposal',
    };

    return entityTypeMap[entityType] || null;
  }
}
